import React, { useState, useEffect, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const MODES = ["Competitivo", "Collaborativo", "Semi-collaborativo", "Solitario"];
const VENUES = ["Bibliocronache", "Patrogames"];

const DEFAULT_FORM = {
  game_name: "",
  categories: [],
  game_mode: "Competitivo",
  venue: "Bibliocronache",
  min_players: 2,
  max_players: 4,
  extra_players: 0,
  explanation_time: 15,
  play_time: 60,
  creator_plays: true,
  notes: "",
  game_date: "",
  bgg_id: null,
  bgg_thumbnail: null,
  bgg_image: null,
};

export default function TableFormDialog({ mode = "create", initial = null, trigger, onSaved }) {
  const isEdit = mode === "edit";
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);

  // BGG search state
  const [bggQuery, setBggQuery] = useState("");
  const [bggResults, setBggResults] = useState([]);
  const [bggLoading, setBggLoading] = useState(false);
  const [bggSelected, setBggSelected] = useState(null); // { name, thumbnail }
  const [bggChips, setBggChips] = useState([]); // tutte le categorie+meccaniche BGG disponibili
  const [tagInput, setTagInput] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (open) {
      if (isEdit && initial) {
        setForm({
          game_name: initial.game_name || "",
          categories: initial.categories || (initial.category ? [initial.category] : ["Eurogame"]),
          game_mode: initial.game_mode || "Competitivo",
          venue: initial.venue || "Bibliocronache",
          min_players: initial.min_players ?? 2,
          max_players: initial.max_players ?? 4,
          extra_players: initial.extra_players ?? 0,
          explanation_time: initial.explanation_time ?? 15,
          play_time: initial.play_time ?? 60,
          creator_plays: initial.creator_plays ?? true,
          notes: initial.notes || "",
          game_date: initial.game_date || "",
          bgg_id: initial.bgg_id || null,
          bgg_thumbnail: initial.bgg_thumbnail || null,
          bgg_image: initial.bgg_image || null,
        });
      } else {
        setForm(DEFAULT_FORM);
        setBggQuery("");
        setBggSelected(null);
        setBggResults([]);
        setBggChips([]);
        setTagInput("");
      }
    }
  }, [open, isEdit, initial]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const searchBgg = useCallback((q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.length < 2) { setBggResults([]); setShowDropdown(false); return; }
    debounceRef.current = setTimeout(async () => {
      setBggLoading(true);
      try {
        const res = await api.get(`/bgg/search?q=${encodeURIComponent(q)}`);
        setBggResults(res.data);
        setShowDropdown(res.data.length > 0);
      } catch {
        setBggResults([]);
      } finally {
        setBggLoading(false);
      }
    }, 400);
  }, []);

  const selectBggGame = async (result) => {
    setShowDropdown(false);
    setBggQuery(result.name);
    setBggLoading(true);
    try {
      const res = await api.get(`/bgg/game/${result.id}`);
      const g = res.data;
      setBggSelected({ name: g.name, thumbnail: g.thumbnail });
      const allChips = [...(g.categories || []), ...(g.mechanics || [])];
      setBggChips(allChips);
      setForm((p) => ({
        ...p,
        game_name: g.name,
        min_players: g.min_players > 0 ? Math.min(Math.max(g.min_players, 1), 12) : p.min_players,
        max_players: g.max_players > 0 ? Math.min(Math.max(g.max_players, 2), 12) : p.max_players,
        play_time: g.play_time > 0 ? Math.min(Math.max(g.play_time, 5), 1440) : p.play_time,
        categories: g.categories?.length > 0 ? g.categories.slice(0, 3) : p.categories,
        bgg_id: result.id,
        bgg_thumbnail: g.thumbnail || null,
        bgg_image: g.image || null,
      }));
      toast.success(`"${g.name}" importato da BGG`);
    } catch {
      toast.error("Impossibile caricare i dettagli da BGG");
    } finally {
      setBggLoading(false);
    }
  };

  const clearBgg = () => {
    setBggQuery("");
    setBggSelected(null);
    setBggResults([]);
    setBggChips([]);
    setTagInput("");
    setShowDropdown(false);
    setForm(DEFAULT_FORM);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.categories || form.categories.length === 0) {
      toast.error("Seleziona almeno una categoria (o cerca il gioco su BGG).");
      return;
    }
    setBusy(true);
    try {
      const { categories, ...rest } = form;
      const payload = {
        ...rest,
        categories,
        min_players: Number(form.min_players),
        max_players: Number(form.max_players),
        extra_players: Number(form.extra_players),
        explanation_time: Number(form.explanation_time),
        play_time: Number(form.play_time),
        game_date: form.game_date || null,
        bgg_id: rest.bgg_id ? Number(rest.bgg_id) : null,
      };
      if (isEdit) {
        const { creator_plays, ...rest } = payload;
        await api.put(`/tables/${initial.id}`, rest);
        toast.success("Tavolo aggiornato!");
      } else {
        await api.post("/tables", payload);
        toast.success("Tavolo creato!");
      }
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const defaultTrigger = (
    <Button
      data-testid="open-create-table-button"
      className="bg-[#2B593F] hover:bg-[#1E3F2C] text-[#FDFBF7] rounded-xl px-6 h-12 text-base font-semibold shadow-sm transition-transform hover:-translate-y-0.5"
    >
      <Plus size={20} className="mr-2" /> Crea Tavolo
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || defaultTrigger}</DialogTrigger>
      <DialogContent className="bg-white dark:bg-[#111111] rounded-3xl border border-[#EAE3D9] dark:border-[#2A2A2A] w-[calc(100%-1.5rem)] sm:max-w-lg p-0 overflow-hidden max-h-[92vh] flex flex-col">
        <div className="px-5 sm:px-6 pt-5 sm:pt-6 flex-shrink-0">
          <DialogHeader>
            <DialogTitle className="font-display text-xl sm:text-2xl text-[#2D2A26] dark:text-[#F5F1EA]">
              {isEdit ? "Modifica tavolo" : "Crea un nuovo tavolo"}
            </DialogTitle>
            <DialogDescription className="text-[#757169] dark:text-[#B8B2AA] text-sm">
              {isEdit
                ? "Aggiorna i dettagli del tuo tavolo."
                : "Imposta gioco, durata e numero di posti — gli altri si uniranno."}
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={submit} className="px-5 sm:px-6 py-5 sm:py-6 space-y-4 overflow-y-auto flex-1">

          {/* BGG search — solo in modalità crea */}
          {!isEdit && (
            <div ref={dropdownRef} className="relative">
              <Label className="text-xs uppercase tracking-[0.1em] text-[#757169] dark:text-[#B8B2AA] font-medium flex items-center gap-1.5">
                <Search size={11} /> Cerca su BoardGameGeek
              </Label>
              <div className="relative mt-2">
                <Input
                  value={bggQuery}
                  onChange={(e) => { setBggQuery(e.target.value); searchBgg(e.target.value); }}
                  placeholder="Es. Terraforming Mars, Wingspan…"
                  className="bg-[#FDFBF7] dark:bg-[#1A1A1A] border-[#EAE3D9] dark:border-[#2A2A2A] dark:text-[#F5F1EA] focus-visible:ring-[#2B593F]/30 focus-visible:border-[#2B593F] rounded-xl h-11 pr-8"
                />
                {bggSelected && (
                  <button type="button" onClick={clearBgg} className="absolute right-3 top-3 text-[#757169] hover:text-[#2D2A26]">
                    <X size={14} />
                  </button>
                )}
              </div>
              {bggLoading && (
                <p className="text-[11px] text-[#757169] dark:text-[#B8B2AA] mt-1">Ricerca in corso…</p>
              )}
              {bggSelected && (
                <div className="flex items-center gap-2 mt-2 p-2 rounded-lg bg-[#F0F7F3] border border-[#2B593F]/20">
                  {bggSelected.thumbnail && (
                    <img src={bggSelected.thumbnail} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
                  )}
                  <p className="text-xs text-[#2B593F] font-medium truncate">{bggSelected.name} — dati importati da BGG</p>
                </div>
              )}
              {showDropdown && (
                <ul className="absolute z-50 w-full mt-1 bg-white dark:bg-[#1A1A1A] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-xl shadow-lg overflow-hidden max-h-52 overflow-y-auto">
                  {bggResults.map((r) => (
                    <li
                      key={r.id}
                      onMouseDown={() => selectBggGame(r)}
                      className="px-4 py-2.5 cursor-pointer hover:bg-[#F5F1EA] dark:hover:bg-[#252525] text-sm text-[#2D2A26] dark:text-[#F5F1EA] flex justify-between items-center"
                    >
                      <span>{r.name}</span>
                      {r.year && <span className="text-[#757169] dark:text-[#B8B2AA] text-xs ml-2 flex-shrink-0">{r.year}</span>}
                    </li>
                  ))}
                </ul>
              )}
              {!bggSelected && (
                <p className="text-[11px] text-[#757169] dark:text-[#B8B2AA] mt-1.5">Cerca un gioco per compilare automaticamente i campi.</p>
              )}
              <div className="flex justify-end mt-1">
                <a href="https://boardgamegeek.com" target="_blank" rel="noopener noreferrer">
                  <img
                    src="/images/poweredbybgg.webp"
                    alt="Powered by BGG"
                    className="h-4 opacity-60 hover:opacity-100 transition-opacity"
                  />
                </a>
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-[0.1em] text-[#757169] dark:text-[#B8B2AA] font-medium">Nome del gioco</Label>
            <Input
              required
              value={form.game_name}
              onChange={(e) => setField("game_name", e.target.value)}
              data-testid="game-name-input"
              placeholder="Es. Terraforming Mars"
              className="mt-2 bg-[#FDFBF7] dark:bg-[#1A1A1A] border-[#EAE3D9] dark:border-[#2A2A2A] dark:text-[#F5F1EA] focus-visible:ring-[#2B593F]/30 focus-visible:border-[#2B593F] rounded-xl h-11"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.1em] text-[#757169] dark:text-[#B8B2AA] font-medium">Luogo</Label>
            <Select value={form.venue} onValueChange={(v) => setField("venue", v)}>
              <SelectTrigger data-testid="venue-select" className="mt-2 bg-[#FDFBF7] dark:bg-[#1A1A1A] border-[#EAE3D9] dark:border-[#2A2A2A] dark:text-[#F5F1EA] rounded-xl h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                {VENUES.map((v) => (
                  <SelectItem key={v} value={v} data-testid={`venue-option-${v}`}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-[#757169] dark:text-[#B8B2AA] mt-1.5">
              {form.venue === "Bibliocronache"
                ? "I tavoli vengono resettati ogni mercoledì alle 04:00."
                : "I tavoli vengono resettati ogni sabato alle 04:00."}
            </p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.1em] text-[#757169] dark:text-[#B8B2AA] font-medium">
              Categorie & Meccaniche
            </Label>
            {/* Chips da BGG — selezionabili/deselezionabili */}
            {bggChips.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {bggChips.map((c) => {
                  const selected = form.categories.includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => {
                        if (selected) {
                          if (form.categories.length === 1) return;
                          setField("categories", form.categories.filter((x) => x !== c));
                        } else {
                          setField("categories", [...form.categories, c]);
                        }
                      }}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                        selected
                          ? "bg-[#2B593F] text-[#FDFBF7] border-[#2B593F]"
                          : "bg-[#F5F1EA] dark:bg-[#1A1A1A] text-[#4A4742] dark:text-[#C8C2BA] border-[#EAE3D9] dark:border-[#2A2A2A] hover:border-[#2B593F] hover:text-[#2B593F] dark:hover:text-[#8FC79F]"
                      }`}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            )}
            {/* Tag selezionati (con X per rimuovere) */}
            {form.categories.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {form.categories.map((c) => (
                  <span key={c} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-[#2B593F]/10 text-[#2B593F] dark:bg-[#2B593F]/20 dark:text-[#8FC79F] border border-[#2B593F]/20">
                    {c}
                    <button
                      type="button"
                      onClick={() => {
                        if (form.categories.length === 1) return;
                        setField("categories", form.categories.filter((x) => x !== c));
                      }}
                      className="hover:text-red-500 transition-colors"
                    >
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {/* Input libero per aggiungere tag manualmente */}
            <div className="flex gap-2 mt-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === ",") && tagInput.trim()) {
                    e.preventDefault();
                    const tag = tagInput.trim().replace(/,$/, "");
                    if (tag && !form.categories.includes(tag)) {
                      setField("categories", [...form.categories, tag]);
                    }
                    setTagInput("");
                  }
                }}
                placeholder="Aggiungi tag personalizzato…"
                className="bg-[#FDFBF7] dark:bg-[#1A1A1A] border-[#EAE3D9] dark:border-[#2A2A2A] dark:text-[#F5F1EA] focus-visible:ring-[#2B593F]/30 focus-visible:border-[#2B593F] rounded-xl h-9 text-sm"
              />
              <button
                type="button"
                onClick={() => {
                  const tag = tagInput.trim();
                  if (tag && !form.categories.includes(tag)) {
                    setField("categories", [...form.categories, tag]);
                  }
                  setTagInput("");
                }}
                className="px-3 h-9 rounded-xl bg-[#F5F1EA] dark:bg-[#252525] text-[#4A4742] dark:text-[#C8C2BA] text-xs font-medium hover:bg-[#EAE3D9] dark:hover:bg-[#2A2A2A] transition-colors"
              >
                Aggiungi
              </button>
            </div>
            <p className="text-[11px] text-[#757169] dark:text-[#B8B2AA] mt-1.5">
              {bggChips.length > 0 ? "Clicca le chips per selezionare/deselezionare. " : ""}Oppure scrivi e premi Invio per aggiungere tag liberi.
            </p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.1em] text-[#757169] dark:text-[#B8B2AA] font-medium">Modalità</Label>
            <Select value={form.game_mode} onValueChange={(v) => setField("game_mode", v)}>
              <SelectTrigger data-testid="mode-select" className="mt-2 bg-[#FDFBF7] dark:bg-[#1A1A1A] border-[#EAE3D9] dark:border-[#2A2A2A] dark:text-[#F5F1EA] rounded-xl h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                {MODES.map((m) => (
                  <SelectItem key={m} value={m} data-testid={`mode-option-${m}`}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.1em] text-[#757169] dark:text-[#B8B2AA] font-medium">Min</Label>
                <Input
                  type="number"
                  min={1}
                  max={12}
                  value={form.min_players}
                  onChange={(e) => setField("min_players", e.target.value)}
                  data-testid="min-players-input"
                  className="mt-2 bg-[#FDFBF7] dark:bg-[#1A1A1A] border-[#EAE3D9] dark:border-[#2A2A2A] dark:text-[#F5F1EA] focus-visible:ring-[#2B593F]/30 focus-visible:border-[#2B593F] rounded-xl h-11"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.1em] text-[#757169] dark:text-[#B8B2AA] font-medium">Max</Label>
                <Input
                  type="number"
                  min={2}
                  max={12}
                  required
                  value={form.max_players}
                  onChange={(e) => setField("max_players", e.target.value)}
                  data-testid="max-players-input"
                  className="mt-2 bg-[#FDFBF7] dark:bg-[#1A1A1A] border-[#EAE3D9] dark:border-[#2A2A2A] dark:text-[#F5F1EA] focus-visible:ring-[#2B593F]/30 focus-visible:border-[#2B593F] rounded-xl h-11"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.1em] text-[#757169] dark:text-[#B8B2AA] font-medium">Già occupati</Label>
                <Input
                  type="number"
                  min={0}
                  max={Math.max(0, Number(form.max_players) - 1)}
                  value={form.extra_players}
                  onChange={(e) => setField("extra_players", e.target.value)}
                  data-testid="extra-players-input"
                  className="mt-2 bg-[#FDFBF7] dark:bg-[#1A1A1A] border-[#EAE3D9] dark:border-[#2A2A2A] dark:text-[#F5F1EA] focus-visible:ring-[#2B593F]/30 focus-visible:border-[#2B593F] rounded-xl h-11"
                />
              </div>
            </div>
            <p className="text-[11px] text-[#757169] dark:text-[#B8B2AA] mt-1.5">Giocatori: min–max da BGG. "Già occupati": amici fuori app che vengono con te.</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] text-[#757169] dark:text-[#B8B2AA] font-medium">Spiegazione (min)</Label>
              <Input
                type="number"
                min={0}
                max={120}
                required
                value={form.explanation_time}
                onChange={(e) => setField("explanation_time", e.target.value)}
                data-testid="explanation-time-input"
                className="mt-2 bg-[#FDFBF7] dark:bg-[#1A1A1A] border-[#EAE3D9] dark:border-[#2A2A2A] dark:text-[#F5F1EA] focus-visible:ring-[#2B593F]/30 focus-visible:border-[#2B593F] rounded-xl h-11"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] text-[#757169] dark:text-[#B8B2AA] font-medium">Durata (min)</Label>
              <Input
                type="number"
                min={5}
                max={1440}
                required
                value={form.play_time}
                onChange={(e) => setField("play_time", e.target.value)}
                data-testid="play-time-input"
                className="mt-2 bg-[#FDFBF7] dark:bg-[#1A1A1A] border-[#EAE3D9] dark:border-[#2A2A2A] dark:text-[#F5F1EA] focus-visible:ring-[#2B593F]/30 focus-visible:border-[#2B593F] rounded-xl h-11"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.1em] text-[#757169] dark:text-[#B8B2AA] font-medium">Note (opzionale)</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              data-testid="notes-input"
              placeholder="Es. Espansione X inclusa, livello esperto..."
              className="mt-2 bg-[#FDFBF7] dark:bg-[#1A1A1A] border-[#EAE3D9] dark:border-[#2A2A2A] dark:text-[#F5F1EA] focus-visible:ring-[#2B593F]/30 focus-visible:border-[#2B593F] rounded-xl"
              rows={3}
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.1em] text-[#757169] dark:text-[#B8B2AA] font-medium">Data serata (opzionale)</Label>
            <input
              type="date"
              value={form.game_date}
              min={new Date().toISOString().split("T")[0]}
              onChange={(e) => setField("game_date", e.target.value)}
              className="mt-2 w-full bg-[#FDFBF7] dark:bg-[#1A1A1A] border border-[#EAE3D9] dark:border-[#2A2A2A] dark:text-[#F5F1EA] focus:outline-none focus:border-[#2B593F] rounded-xl h-11 px-3 text-sm"
            />
            <p className="text-[11px] text-[#757169] dark:text-[#B8B2AA] mt-1.5">
              Se impostata, il tavolo non verrà cancellato dallo script automatico fino a dopo la data indicata.
            </p>
          </div>

          {!isEdit && (
            <label className="flex items-start gap-3 p-4 rounded-xl bg-[#F5F1EA] dark:bg-[#1A1A1A] border border-[#EAE3D9] dark:border-[#2A2A2A] cursor-pointer">
              <Checkbox
                checked={form.creator_plays}
                onCheckedChange={(v) => setField("creator_plays", Boolean(v))}
                data-testid="creator-plays-checkbox"
                className="mt-0.5 data-[state=checked]:bg-[#2B593F] data-[state=checked]:border-[#2B593F]"
              />
              <div>
                <p className="text-sm font-medium text-[#2D2A26] dark:text-[#F5F1EA]">Partecipo anche io</p>
                <p className="text-xs text-[#757169] dark:text-[#B8B2AA]">Se attivo, occupi automaticamente un posto al tavolo.</p>
              </div>
            </label>
          )}

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpen(false)}
              className="flex-1 h-11 rounded-xl text-[#2D2A26] dark:text-[#F5F1EA] hover:bg-[#F5F1EA] dark:hover:bg-[#1A1A1A]"
              data-testid="form-cancel"
            >
              Annulla
            </Button>
            <Button
              type="submit"
              disabled={busy}
              data-testid={isEdit ? "edit-table-submit" : "create-table-submit"}
              className="flex-1 h-11 bg-[#2B593F] hover:bg-[#1E3F2C] text-[#FDFBF7] rounded-xl font-medium"
            >
              {busy ? "Salvataggio..." : isEdit ? "Salva modifiche" : "Crea Tavolo"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
